/**
 * mentionParser — EFISSUE-830 helper for the composer's @-mention dropdown.
 *
 * Pure, no React. Given the textarea's full content + cursor position,
 * decides whether the user is currently typing an @-mention and, if so,
 * returns the query string + the start index of the `@` so the composer
 * can replace it on selection.
 *
 * Triggering rules (ASCII-only for V1):
 *   - The `@` must be at the very start of the content OR preceded by
 *     whitespace (newline / tab / space). `email@foo` typed in the body
 *     does NOT trigger.
 *   - The text between the `@` and the cursor must contain no whitespace
 *     and no additional `@`. Multiple `@`s in flight → the most recent
 *     wins (typical "I typed @x then @y").
 *   - Cursor must be AT or AFTER the `@` (not before it).
 *
 * Returned `query` is whatever's between `@` and cursor (could be empty
 * for "user just typed @"). The composer uses it as a prefix filter.
 */

export interface MentionMatch {
  /** Index of the `@` character in the input. */
  start: number;
  /** Substring between `@` and cursor; `""` immediately after typing `@`. */
  query: string;
}

const WORD_RE = /^[A-Za-z0-9_-]*$/;

export function parseMentionAt(text: string, cursorPos: number): MentionMatch | null {
  if (cursorPos < 0 || cursorPos > text.length) return null;
  // Walk backwards from cursor looking for `@`. Bail on whitespace or
  // a second `@` in the same window — they break the mention region.
  for (let i = cursorPos - 1; i >= 0; i--) {
    const ch = text[i];
    if (ch === "@") {
      const before = i === 0 ? "" : text[i - 1];
      if (before !== "" && !/\s/.test(before)) return null;
      const query = text.slice(i + 1, cursorPos);
      if (!WORD_RE.test(query)) return null;
      return { start: i, query };
    }
    if (/\s/.test(ch)) return null;
  }
  return null;
}
